import { useEffect, useRef } from "react";
import { RiDeleteBinLine } from "react-icons/ri";
import { IoClose } from "react-icons/io5";

type DeleteConnectionModalProps = {
  isOpen: boolean;
  connectionName: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isDeleting?: boolean;
};

const DeleteConnectionModal = ({
  isOpen,
  connectionName,
  onConfirm,
  onCancel,
  isDeleting = false,
}: DeleteConnectionModalProps) => {
  const modalRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.showModal();
    } else if (!isOpen && modalRef.current) {
      modalRef.current.close();
    }
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === modalRef.current) {
      onCancel();
    }
  };

  return (
    <dialog
      ref={modalRef}
      className="modal"
      onClick={handleBackdropClick}
    >
      <div className="modal-box">
        {/* Close Button */}
        <button
          onClick={onCancel}
          disabled={isDeleting}
          className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
        >
          <IoClose className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 bg-error/10 rounded-full flex items-center justify-center">
            <RiDeleteBinLine className="w-6 h-6 text-error" />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-center mb-2">
          Delete Connection?
        </h3>

        {/* Description */}
        <div className="text-center mb-6">
          <p className="text-base-content/70 text-sm leading-relaxed mb-1">
            Are you sure you want to delete{" "}
            <span className="font-medium text-base-content">"{connectionName}"</span>?
          </p>
          <p className="text-base-content/50 text-xs">
            This action cannot be undone.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="modal-action justify-center gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="btn btn-outline flex-1"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="btn btn-error flex-1"
          >
            {isDeleting ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Deleting...
              </>
            ) : (
              <>
                <RiDeleteBinLine className="w-4 h-4" />
                Delete
              </>
            )}
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default DeleteConnectionModal;